/**
 * The long-format record sheet that stands beside a mannequin pulled out for review: tall,
 * see-through canvas pages with every section of the subject's record one below the other.
 * When the record is longer than one page, the next page stands to the right of the last,
 * so the whole record is always readable without scrolling. Selecting a row opens that
 * record in the profile panel.
 */
import * as THREE from 'three';
import { CanvasPanel, type Region } from '../dashboards/CanvasPanel.ts';
import { PALETTE, fillRoundRect, font, hexToRgba, text } from '../dashboards/charts.ts';
import type { StudyView } from '../dashboards/StudyView.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import type { PanelPose } from './layout.ts';
import {
  SHEET_LAYOUT,
  cellWeight,
  paginateSheet,
  sheetSections,
  wrapCell,
  type Measure,
  type SheetItem,
  type SheetSection,
} from './sheet.ts';

export const SHEET_W = 0.9;
export const SHEET_H = 2.3;
/** Pages kept ready; a record that needs more shows a note on the last one. */
export const MAX_PAGES = 4;

export class RecordSheet {
  readonly group = new THREE.Group();
  private readonly panels: CanvasPanel[] = [];
  private subjectId: string | undefined;
  private sections: SheetSection[] = [];
  private pages: SheetItem[][] = [];
  private columns: { x: number; w: number }[][] = [];
  private subtitle: string[] = [];
  /** Pages the record needed before the cap. */
  private needed = 0;

  constructor(
    private readonly view: StudyView,
    private readonly interactive: InteractiveSystem,
    private readonly handlers: {
      open(subjectId: string, path: string[]): void;
      tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
    },
  ) {
    this.group.name = 'ward-record-sheet';
    for (let i = 0; i < MAX_PAGES; i++) {
      const panel = new CanvasPanel({
        width: SHEET_W,
        height: SHEET_H,
        pixelWidth: SHEET_LAYOUT.width,
        pixelHeight: SHEET_LAYOUT.height,
        name: `ward-record-sheet-${i}`,
        resolution: 1.5,
      });
      (panel.mesh.material as THREE.MeshBasicMaterial).transparent = true;
      panel.mesh.visible = false;
      panel.setDraw((ctx, p) => this.draw(ctx, p, i));
      panel.onSelect = (region) => this.select(region);
      panel.onHoverChange = (region, point) =>
        handlers.tooltip(region?.tooltip, region ? point : undefined);
      panel.attach(interactive);
      this.group.add(panel.mesh);
      this.panels.push(panel);
    }
  }

  /** The first page (for placement and visibility checks). */
  get mesh(): THREE.Mesh {
    return this.panels[0]!.mesh;
  }

  get subject(): string | undefined {
    return this.subjectId;
  }

  /** Pages currently showing. */
  get pageCount(): number {
    return this.panels.filter((p) => p.mesh.visible).length;
  }

  /** Lays the subject's record out into pages; returns how many pages it needs (capped). */
  load(subjectId: string): number {
    this.subjectId = subjectId;
    this.sections = sheetSections(this.view, subjectId);
    const ctx = this.panels[0]!.ctx;
    // The same font the page is drawn with, so wrapped lines always fit their columns.
    const measure: Measure = (s, size, weight) => {
      ctx.font = font(size, weight);
      return ctx.measureText(s).width;
    };
    const subject = this.view.subject(subjectId);
    const site = subject ? this.view.site(subject.siteId) : undefined;
    this.subtitle = subject
      ? wrapCell(
          `${subject.sex === 'F' ? 'Female' : 'Male'} ${subject.age} · Site ${subject.siteId}${site ? ` ${site.city}` : ''} · ${subject.arm ?? 'not randomised'} · ${subject.status}`,
          SHEET_LAYOUT.width - 2 * SHEET_LAYOUT.pad,
          (t) => measure(t, 13, 500),
        )
      : [];
    const layout = paginateSheet(this.sections, measure, SHEET_LAYOUT, this.subtitle.length);
    this.columns = layout.columns;
    this.needed = layout.pages.length;
    this.pages = layout.pages.slice(0, MAX_PAGES);
    return this.pages.length;
  }

  /** Stands each page at its pose; pages without a pose are hidden. */
  place(poses: PanelPose[]): void {
    this.panels.forEach((panel, i) => {
      const pose = poses[i];
      if (i < this.pages.length && pose) {
        panel.mesh.position.set(pose.x, pose.y, pose.z);
        panel.mesh.rotation.set(0, pose.yaw, 0);
        panel.mesh.scale.setScalar(pose.scale);
        panel.mesh.visible = true;
        panel.invalidate();
      } else panel.mesh.visible = false;
    });
  }

  /** Moves the visible pages (a dragged cockpit) without redrawing them. */
  move(poses: PanelPose[]): void {
    this.panels.forEach((panel, i) => {
      const pose = poses[i];
      if (!pose || !panel.mesh.visible) return;
      panel.mesh.position.set(pose.x, pose.y, pose.z);
      panel.mesh.rotation.set(0, pose.yaw, 0);
    });
  }

  hide(): void {
    this.subjectId = undefined;
    this.pages = [];
    for (const p of this.panels) p.mesh.visible = false;
  }

  update(): void {
    for (const p of this.panels) if (p.mesh.visible) p.update();
  }

  /** Regions on every visible page, for dev hooks and tests. */
  allRegions(): { id: string; data: unknown }[] {
    return this.panels
      .filter((p) => p.mesh.visible)
      .flatMap((p) => p.allRegions().map((r) => ({ id: r.id, data: r.data })));
  }

  regionCount(): number {
    return this.allRegions().length;
  }

  /** Selects a region by id as a ray would (dev hook). */
  selectRegion(id: string): boolean {
    for (const p of this.panels) {
      if (!p.mesh.visible) continue;
      const region = p.allRegions().find((r) => r.id === id);
      if (region) {
        this.select(region);
        return true;
      }
    }
    return false;
  }

  private draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel, index: number): void {
    const items = this.pages[index];
    const s = this.subjectId ? this.view.subject(this.subjectId) : undefined;
    if (!items || !s) return;
    const L = SHEET_LAYOUT;
    // See-through: a faint tint so the text stays legible over a bright room, no frame.
    fillRoundRect(ctx, { x: 0, y: 0, w: L.width, h: L.height }, 16, PALETTE.sheet);
    ctx.shadowColor = PALETTE.sheetShadow;
    ctx.shadowBlur = 3;
    for (const item of items) {
      switch (item.kind) {
        case 'title': {
          if (item.page === 0) {
            text(ctx, `${s.id} · complete record`, L.pad, 34, { size: 22, weight: 800 });
            this.subtitle.forEach((line, i) =>
              text(ctx, line, L.pad, 56 + i * L.subtitleLineH, {
                size: 13,
                colour: PALETTE.muted,
              }),
            );
          } else
            text(ctx, `${s.id} · complete record, continued`, L.pad, 30, {
              size: 16,
              weight: 700,
            });
          if (item.pages > 1)
            text(ctx, `page ${item.page + 1} of ${item.pages}`, L.width - L.pad, 30, {
              size: 12,
              colour: PALETTE.teal,
              align: 'right',
            });
          break;
        }
        case 'section': {
          const section = this.sections[item.section]!;
          fillRoundRect(
            ctx,
            { x: 10, y: item.y + 2, w: L.width - 20, h: item.h - 4 },
            6,
            'rgba(63,214,255,0.28)',
          );
          text(
            ctx,
            `${section.title.toUpperCase()}  ·  ${section.rows.length}${item.continued ? '  (continued)' : ''}`,
            20,
            item.y + 20,
            { size: 12, weight: 700, colour: PALETTE.text },
          );
          break;
        }
        case 'header': {
          const cols = this.columns[item.section]!;
          item.lines.forEach((lines, i) =>
            lines.forEach((line, li) =>
              text(ctx, line, cols[i]!.x, item.y + 12 + li * L.headerLineH, {
                size: L.headerSize,
                weight: 600,
                colour: PALETTE.muted,
              }),
            ),
          );
          break;
        }
        case 'empty':
          text(ctx, 'none recorded', 20, item.y + 15, { size: 11, colour: PALETTE.muted });
          break;
        case 'row': {
          const section = this.sections[item.section]!;
          const row = section.rows[item.row]!;
          const cols = this.columns[item.section]!;
          const id = `row:${item.section}:${item.row}`;
          const r = { x: 12, y: item.y, w: L.width - 24, h: item.h };
          if (panel.isHovered(id)) fillRoundRect(ctx, r, 4, PALETTE.hover);
          if (row.flag) {
            ctx.fillStyle = PALETTE.amber;
            ctx.fillRect(r.x, r.y + 3, 3, item.h - 6);
          }
          item.lines.forEach((lines, ci) => {
            const col = cols[ci]!;
            const tint = row.cellColours?.[ci];
            if (tint && lines[0]) {
              ctx.shadowBlur = 0;
              fillRoundRect(
                ctx,
                { x: col.x - 3, y: item.y + 2, w: col.w - 2, h: item.h - 4 },
                3,
                hexToRgba(tint, 0.45),
              );
              ctx.shadowBlur = 3;
            }
            lines.forEach((line, li) => {
              text(ctx, line, col.x, item.y + 13 + li * L.lineH, {
                size: L.size,
                weight: cellWeight(ci),
                colour: ci === 0 ? PALETTE.text : PALETTE.textSoft,
              });
            });
          });
          panel.region({
            id,
            ...r,
            data: row.path,
            tooltip: [
              row.cells[0] ?? section.title,
              row.path.length ? 'Open in the profile' : 'Demographics in the profile',
            ],
          });
          break;
        }
      }
    }
    if (index === this.pages.length - 1 && this.needed > this.pages.length)
      text(
        ctx,
        `${this.needed - this.pages.length} more pages: open the profile tabs for the rest`,
        L.pad,
        L.height - 8,
        { size: 11, colour: PALETTE.amber },
      );
    ctx.shadowBlur = 0;
  }

  private select(region: Region): void {
    if (!this.subjectId) return;
    this.handlers.open(this.subjectId, [`subject:${this.subjectId}`, ...(region.data as string[])]);
  }

  dispose(): void {
    for (const p of this.panels) {
      p.detach(this.interactive);
      p.dispose();
    }
  }
}
